import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    CardMedia,
    Container,
    Grid,
    Typography,
} from "@mui/material";
import { LocationOn } from "@mui/icons-material";
import { Helmet } from "react-helmet-async";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";

import Navbar from "../Navbar/Navbar";
import Footer from "../Footer/Footer";
import config from "../../Config";


const ExploreAll = () => {
    const { collectionId } = useParams();

    const navigate = useNavigate();

    const [collection, setCollection] = useState(null);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchCollectionProducts = async () => {
            try {
                setLoading(true);

                const response = await axios.get(
                    `${config.apiBaseUrl}/${config.exploreAll}`,
                    {
                        params: {
                            type: "collection",
                            value: collectionId,
                        },
                    }
                );

                if (!response?.data?.success) {
                    setCollection(null);
                    setProducts([]);
                    return;
                }

                const data = response.data;

                const formattedProducts = Array.isArray(data.products)
                    ? data.products.map((product) => ({
                        ...product,
                        city: product?.address?.city || "",
                        availableDate:
                            product?.slots?.availableDate || "",
                    }))
                    : [];

                setCollection({
                    collectionId,
                    collectionTitle:
                        data.title || "Collection",
                });

                setProducts(formattedProducts);
            } catch (error) {
                console.error(
                    "Error fetching collection products:",
                    error,
                );

                setCollection(null);
                setProducts([]);
            } finally {
                setLoading(false);
            }
        };

        if (collectionId) {
            fetchCollectionProducts();
        }
    }, [collectionId]);

    const handleProductClick = (productId) => {
        if (!productId) return;

        navigate(`/ProductDetail/${productId}`);
    };

    return (
        <Box
            sx={{
                backgroundColor: "#fff",
                color: "#171717",
                minHeight: "100vh",
            }}
        >
            <Helmet>
                <title>
                    {collection?.collectionTitle
                        ? `${collection.collectionTitle} | Hindustan Wardrobe`
                        : "Explore Collection | Hindustan Wardrobe"}
                </title>

                <meta
                    name="description"
                    content={
                        collection?.collectionTitle
                            ? `Explore the complete ${collection.collectionTitle} collection at Hindustan Wardrobe. Rent premium fashion for your next occasion.`
                            : "Explore premium fashion collections at Hindustan Wardrobe."
                    }
                />
            </Helmet>

            <Navbar />

            {/* ================= HEADER ================= */}

            <Box
                sx={{
                    backgroundColor: "#f7f5f1",
                    py: {
                        xs: 7,
                        md: 10,
                    },
                }}
            >
                <Container maxWidth="lg">
                    <Box
                        sx={{
                            textAlign: "center",
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: "0.72rem",
                                letterSpacing: 3,
                                textTransform: "uppercase",
                                color: "text.secondary",
                                mb: 1.5,
                            }}
                        >
                            Explore Collection
                        </Typography>

                        <Typography
                            component="h1"
                            sx={{
                                fontSize: {
                                    xs: "2.3rem",
                                    sm: "3rem",
                                    md: "3.8rem",
                                },
                                fontWeight: 600,
                                lineHeight: 1.1,
                                mb: 2,
                            }}
                        >
                            {collection?.collectionTitle || "Collection"}
                        </Typography>

                        <Typography
                            sx={{
                                maxWidth: 650,
                                mx: "auto",
                                color: "text.secondary",
                                lineHeight: 1.8,
                            }}
                        >
                            Discover every look from this collection and find
                            something made for your next moment.
                        </Typography>
                    </Box>
                </Container>
            </Box>

            {/* ================= PRODUCTS ================= */}

            <Box
                sx={{
                    py: {
                        xs: 7,
                        md: 10,
                    },
                }}
            >
                <Container maxWidth="lg">
                    {loading ? (
                        <Box
                            sx={{
                                py: 10,
                                textAlign: "center",
                            }}
                        >
                            <Typography color="text.secondary">
                                Loading products...
                            </Typography>
                        </Box>
                    ) : !collection ? (
                        <Box
                            sx={{
                                py: 10,
                                textAlign: "center",
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: "1.5rem",
                                    fontWeight: 600,
                                    mb: 1,
                                }}
                            >
                                Collection not found
                            </Typography>

                            <Typography
                                color="text.secondary"
                                sx={{
                                    mb: 3,
                                }}
                            >
                                The collection you're looking for is no longer
                                available.
                            </Typography>

                            <Button
                                variant="outlined"
                                onClick={() => navigate("/")}
                                sx={{
                                    borderColor: "#222",
                                    color: "#222",
                                    borderRadius: 0,
                                    px: 3,
                                    py: 1.2,
                                    textTransform: "uppercase",
                                    letterSpacing: 1,
                                    fontSize: "0.72rem",
                                    "&:hover": {
                                        borderColor: "#222",
                                        backgroundColor: "#222",
                                        color: "#fff",
                                    },
                                }}
                            >
                                Back to Home
                            </Button>
                        </Box>
                    ) : products.length === 0 ? (
                        <Box
                            sx={{
                                py: 10,
                                textAlign: "center",
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: "1.4rem",
                                    fontWeight: 600,
                                    mb: 1,
                                }}
                            >
                                No products available
                            </Typography>

                            <Typography
                                color="text.secondary"
                                sx={{
                                    mb: 3,
                                }}
                            >
                                There are currently no products in this
                                collection.
                            </Typography>

                            <Button
                                variant="outlined"
                                onClick={() => navigate("/")}
                                sx={{
                                    borderColor: "#222",
                                    color: "#222",
                                    borderRadius: 0,
                                    px: 3,
                                    py: 1.2,
                                    textTransform: "uppercase",
                                    letterSpacing: 1,
                                    fontSize: "0.72rem",
                                    "&:hover": {
                                        borderColor: "#222",
                                        backgroundColor: "#222",
                                        color: "#fff",
                                    },
                                }}
                            >
                                Back to Home
                            </Button>
                        </Box>
                    ) : (
                        <>
                            {/* ================= TOOLBAR ================= */}

                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    mb: 5,
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: "0.8rem",
                                        color: "text.secondary",
                                    }}
                                >
                                    {products.length}{" "}
                                    {products.length === 1
                                        ? "Product"
                                        : "Products"}
                                </Typography>

                                <Button
                                    onClick={() => navigate("/")}
                                    sx={{
                                        color: "#222",
                                        textTransform: "uppercase",
                                        letterSpacing: 1,
                                        fontSize: "0.7rem",
                                        "&:hover": {
                                            backgroundColor: "transparent",
                                        },
                                    }}
                                >
                                    ← Back to Home
                                </Button>
                            </Box>

                            {/* ================= PRODUCT GRID ================= */}

                            <Grid container spacing={3}>
                                {products.map((product) => {
                                    const image =
                                        product?.productImage?.[0] || "";

                                    return (
                                        <Grid
                                            item
                                            key={
                                                product._id ||
                                                product.product?.productId ||
                                                product.productId
                                            }
                                            xs={12}
                                            sm={6}
                                            md={4}
                                            lg={3}
                                        >
                                            <Card
                                                elevation={0}
                                                onClick={() =>
                                                    handleProductClick(
                                                        product?.product?.productId ||
                                                        product?.productId,
                                                    )
                                                }
                                                sx={{
                                                    height: "100%",
                                                    cursor: "pointer",
                                                    backgroundColor: "transparent",
                                                    transition:
                                                        "transform 0.35s ease",
                                                    "&:hover": {
                                                        transform: "translateY(-6px)",
                                                    },
                                                }}
                                            >
                                                {/* IMAGE */}

                                                <Box
                                                    sx={{
                                                        position: "relative",
                                                        overflow: "hidden",
                                                        backgroundColor: "#ece9e4",
                                                        borderRadius: 1,
                                                    }}
                                                >
                                                    <CardMedia
                                                        component="img"
                                                        image={image}
                                                        alt={
                                                            product?.product?.title ||
                                                            product?.title ||
                                                            "Fashion product"
                                                        }
                                                        sx={{
                                                            width: "100%",
                                                            height: {
                                                                xs: 400,
                                                                sm: 420,
                                                                md: 440,
                                                            },
                                                            objectFit: "cover",
                                                            display: "block",
                                                            transition:
                                                                "transform 0.5s ease",
                                                            "&:hover": {
                                                                transform:
                                                                    "scale(1.035)",
                                                            },
                                                        }}
                                                    />
                                                </Box>

                                                {/* DETAILS */}

                                                <CardContent
                                                    sx={{
                                                        px: 0.5,
                                                        py: 1.7,
                                                    }}
                                                >
                                                    <Typography
                                                        variant="subtitle1"
                                                        sx={{
                                                            fontWeight: 600,
                                                            mb: 0.6,
                                                            lineHeight: 1.35,
                                                        }}
                                                    >
                                                        {product?.product?.title ||
                                                            product?.title ||
                                                            "Fashion Product"}
                                                    </Typography>

                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            fontWeight: 600,
                                                            mb: 0.8,
                                                        }}
                                                    >
                                                        ₹
                                                        {product?.product?.rentAmount ||
                                                            product?.rentAmount ||
                                                            0}
                                                        /Day
                                                    </Typography>

                                                    {product.city && (
                                                        <Box
                                                            sx={{
                                                                display: "flex",
                                                                alignItems: "center",
                                                                gap: 0.5,
                                                                mb: 0.6,
                                                            }}
                                                        >
                                                            <LocationOn
                                                                sx={{
                                                                    fontSize: 17,
                                                                    color:
                                                                        "text.secondary",
                                                                }}
                                                            />

                                                            <Typography
                                                                variant="body2"
                                                                color="text.secondary"
                                                            >
                                                                {product.city}
                                                            </Typography>
                                                        </Box>
                                                    )}

                                                    {product.availableDate && (
                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                        >
                                                            Available{" "}
                                                            {product.availableDate}
                                                        </Typography>
                                                    )}
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        </>
                    )}
                </Container>
            </Box>

            <Footer />
        </Box>
    );
};

export default ExploreAll;